import React, { useEffect, useState } from 'react'
import { multiFactor, TotpMultiFactorGenerator, TotpSecret } from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { QRCodeSVG } from 'qrcode.react'
import { auth } from '../../firebaseConfig'
import { setToken } from '../../utils/auth'
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { useTranslation } from 'react-i18next'

interface MFATOTPSetupStepProps {
    email: string
    onNeedsReauth: () => void
}

const MFATOTPSetupStep: React.FC<MFATOTPSetupStepProps> = ({ email, onNeedsReauth }) => {
    const { t } = useTranslation()
    const [verificationCode, setVerificationCode] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
    const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null)
    // Firebase rejects enrolling a second TOTP factor once one already exists — so once
    // enroll() has succeeded, a retry (e.g. after the token refresh below fails) must skip
    // straight to refreshing the token again, never call enroll() a second time.
    const [enrolled, setEnrolled] = useState(false)

    useEffect(() => {
        const setupTOTP = async () => {
            console.info('Starting TOTP setup...')
            let setupError: unknown = null

            try {
                const user = auth.currentUser
                if (!user) return

                const session = await multiFactor(user).getSession()
                const secret = await TotpMultiFactorGenerator.generateSecret(session)
                const url = secret.generateQrCodeUrl(email, 'Medendo')

                setQrCodeUrl(url)
                setTotpSecret(secret)
                console.info('TOTP QR code generated successfully.')
            } catch (err) {
                setupError = err
                console.warn('TOTP setup error:', err)
                setError(t('login.mfa_setup_step.setup_error'))
            }

            if (setupError instanceof FirebaseError && setupError.code === 'auth/requires-recent-login') {
                onNeedsReauth()
            }
        }

        setupTOTP()
    }, [email, onNeedsReauth, t])

    const handleCopySecret = () => {
        if (totpSecret?.secretKey) {
            navigator.clipboard.writeText(totpSecret.secretKey)
        }
    }

    const handleVerifyTOTP = async () => {
        const user = auth.currentUser
        if (!user) return

        if (!enrolled) {
            if (!totpSecret) throw new Error('Setup not initiated.')
            try {
                const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
                    totpSecret,
                    verificationCode
                )
                await multiFactor(user).enroll(multiFactorAssertion, 'TOTP Authenticator')
                console.info('TOTP enrollment successful.')
            } catch (err: unknown) {
                console.warn('TOTP verification error:', err)
                setError(t('login.mfa_setup_step.verification_error'))
                return
            }
            setEnrolled(true)
        }

        try {
            // Enrolling a factor doesn't itself push a fresh onIdTokenChanged event, and the
            // app is gated entirely on useAuthStore's idToken (see App.tsx's `if (!idToken)
            // return <Login />`) — without forcing this refresh, nothing would notice the
            // enrollment until the periodic background token refresher next happens to run.
            const idToken = await user.getIdToken(true)
            setToken(idToken)
        } catch (err: unknown) {
            console.warn('Failed to refresh ID token after TOTP enrollment:', err)
            setError(t('login.mfa_setup_step.finalize_error'))
        }
    }

    const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            await handleVerifyTOTP()
        }
    }

    return (
        <Box sx={{ borderRadius: 2, maxWidth: 400, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
                {t('login.mfa_setup_step.title')}
            </Typography>

            {error && <Typography color="error">{error}</Typography>}

            {qrCodeUrl ? (
                <Stack alignItems="center" spacing={2}>
                    <Typography>{t('login.mfa_setup_step.scan_instruction')}</Typography>
                    <QRCodeSVG value={qrCodeUrl} size={200} />
                    <Typography>{t('login.mfa_setup_step.manual_entry_instruction')}</Typography>
                    <TextField
                        fullWidth
                        value={totpSecret?.secretKey || ''}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleCopySecret} edge="end">
                                        <ContentCopy />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Divider />
                    <Typography>{t('login.mfa_setup_step.code_input_instruction')}</Typography>

                    <TextField
                        fullWidth
                        label={t('login.mfa_setup_step.code_label')}
                        variant="outlined"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        onKeyDown={handleKeyPress}
                    />

                    <Button variant="contained" color="primary" onClick={handleVerifyTOTP} disabled={!verificationCode}>
                        {t('login.mfa_setup_step.verify_button')}
                    </Button>
                </Stack>
            ) : (
                <CircularProgress sx={{ mt: 2 }} />
            )}
        </Box>
    )
}

export default MFATOTPSetupStep
